import { useMemo } from "react";
import { useTranslations } from "next-intl";
import {
  ASSIGNABLE_CAPABILITY_CATALOGUE,
  Capability,
  CapabilityGroup,
  expandImplied,
  validateCapabilitySet,
} from "@/types/Capability";
import { IconSquare, IconSquareCheckFilled } from "@tabler/icons-react";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";

/**
 * Grouped checkbox grid for picking capabilities. Ticking a cap with
 * `implies` auto-ticks the implied caps (via {@link expandImplied} on save —
 * the UI shows the implication inline as a hover hint).
 *
 * Surfaces soft warnings (via {@link validateCapabilitySet}) when e.g.
 * `guests:delete` is ticked without `guests:write`, and — fed by the editor
 * through `fieldPrerequisiteWarnings` — when a ticked capability's list action
 * needs a guest field the RESTRICTED allowlist withholds (check-in without
 * `checkedIn`). Both render on the same row marker, so the admin sees the gap
 * where they made the pick, not only down in the field section.
 */
export function CapabilityCatalogueGrid({
  value,
  onChange,
  fieldPrerequisiteWarnings,
}: {
  value: Set<Capability>;
  onChange: (next: Set<Capability>) => void;
  /** Capability → display labels of the fields its action needs but the allowlist lacks. */
  fieldPrerequisiteWarnings?: ReadonlyMap<Capability, string[]>;
}) {
  const t = useTranslations();
  const groups = useMemo(() => {
    const m = new Map<CapabilityGroup, typeof ASSIGNABLE_CAPABILITY_CATALOGUE>();
    for (const desc of ASSIGNABLE_CAPABILITY_CATALOGUE) {
      const list = m.get(desc.group) ?? [];
      list.push(desc);
      m.set(desc.group, list);
    }
    return Array.from(m.entries());
  }, []);

  const warnings = useMemo(() => validateCapabilitySet(value), [value]);
  const warningsByCap = useMemo(() => {
    const m = new Map<Capability, string[]>();
    for (const w of warnings) {
      const list = m.get(w.cap) ?? [];
      list.push(w.missing);
      m.set(w.cap, list);
    }
    if (fieldPrerequisiteWarnings) {
      for (const [cap, labels] of fieldPrerequisiteWarnings) {
        if (labels.length === 0) continue;
        m.set(cap, [...(m.get(cap) ?? []), ...labels]);
      }
    }
    return m;
  }, [warnings, fieldPrerequisiteWarnings]);

  const toggle = (cap: Capability) => {
    const next = new Set(value);
    if (next.has(cap)) {
      next.delete(cap);
    } else {
      next.add(cap);
    }
    // Apply implied-cap closure so the saved set always satisfies the
    // dependency graph; users opting out of an implied cap have to also
    // untick the depending caps explicitly.
    onChange(expandImplied(next));
  };

  // Pure UI affordance: tick (or untick) every cap in a single group at once.
  // No backend consequence — just edits the same caps Set the checkboxes drive.
  const toggleGroup = (descs: typeof ASSIGNABLE_CAPABILITY_CATALOGUE) => {
    const allChecked = descs.every((d) => value.has(d.key));
    const next = new Set(value);
    if (allChecked) {
      for (const d of descs) next.delete(d.key);
    } else {
      for (const d of descs) next.add(d.key);
    }
    onChange(expandImplied(next));
  };

  return (
    <div className="flex flex-col gap-4">
      {groups.map(([group, descs]) => {
        const selectedCount = descs.filter((d) => value.has(d.key)).length;
        const allChecked = selectedCount === descs.length;
        const headingId = `cap-group-${group}`;
        return (
          <div
            key={group}
            role="group"
            aria-labelledby={headingId}
            className="border outline-quaternary rounded-xl overflow-hidden"
          >
            {/* Header bar: title + live count + bulk toggle */}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 surface-secondary border-b outline-quaternary">
              <div className="flex items-center gap-2 min-w-0">
                <h4 id={headingId} className="body-2-bold truncate">
                  {t(`capabilities.${group}._group`, { defaultValue: group })}
                </h4>
                <span className="shrink-0 text-xs font-tertiary tabular-nums border outline-quaternary rounded-full px-2 py-0.5">
                  {selectedCount}/{descs.length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => toggleGroup(descs)}
                className="shrink-0 flex items-center gap-1.5 text-xs font-tertiary hover:font-primary transition-colors"
              >
                {allChecked ? (
                  <IconSquareCheckFilled size={16} className="font-accent" />
                ) : (
                  <IconSquare size={16} stroke={1.5} />
                )}
                {allChecked
                  ? t("capabilities._selectNone", {
                      defaultValue: "Alle abwählen",
                    })
                  : t("capabilities._selectAll", {
                      defaultValue: "Alle auswählen",
                    })}
              </button>
            </div>

            {/* Capability rows */}
            <div className="flex flex-col gap-1 p-2">
              {descs.map((desc) => {
                const checked = value.has(desc.key);
                return (
                  <div
                    key={desc.key}
                    className={`flex items-start gap-2 rounded-lg px-2 py-2 transition-colors ${
                      checked ? "surface-secondary" : "hover:surface-secondary"
                    }`}
                  >
                    {/* Labels wrap on narrow rows — box and the right-hand
                        cluster stay pinned to the FIRST line (min-h-6 = one
                        `body-1-regular` line box) instead of floating to the
                        middle of a two-line label. */}
                    <div className="min-w-0 flex-1">
                      <CheckboxWithValidation
                        isChecked={checked}
                        onCheckChangeAction={() => toggle(desc.key)}
                        size={18}
                        alignStart
                        isInvalid={warningsByCap.has(desc.key)}
                      >
                        {t(desc.i18nKey, { defaultValue: desc.key })}
                      </CheckboxWithValidation>
                    </div>
                    <div className="shrink-0 flex items-center gap-2 min-h-6">
                      {desc.implies && desc.implies.length > 0 && (
                        <span className="text-xs font-quaternary hidden sm:inline">
                          → {desc.implies.join(", ")}
                        </span>
                      )}
                      <span className="px-2 py-0.5 surface-primary border outline-quaternary rounded-md text-xs font-mono">
                        {desc.key}
                      </span>
                    </div>
                    {warningsByCap.has(desc.key) && (
                      <span className="shrink-0 text-xs font-error leading-6">
                        ⚠ {warningsByCap.get(desc.key)!.join(", ")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
